import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leaveApplicationApi } from '../api/leaveApplicationApi';
import { appToast } from '../../../../components/AppToast';

const DEFAULT_FORM_STATE = {
  applyDate: new Date().toISOString().slice(0, 10),
  emp_id: '',
  emp_name: '',
  fromDate: new Date().toISOString().slice(0, 10),
  tillDate: new Date().toISOString().slice(0, 10),
  type: 'FullDay',
  leaveTypeId: '',
  reasonId: '',
  remarks: '',
  days: 1.0,
  status: 'Pending',
};

export function useLeaveApplicationEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editId = id;
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(DEFAULT_FORM_STATE);
  const [balances, setBalances] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load dropdown options
  useEffect(() => {
    leaveApplicationApi
      .getOptions()
      .then((opts) => {
        setEmployeeOptions(opts.employees || []);
        setLeaveTypeOptions(opts.leaveTypes || []);
        setReasonOptions(opts.reasons || []);

        // Preselect defaults for new entry if not editing
        if (!isEdit) {
          setForm((prev) => ({
            ...prev,
            leaveTypeId: prev.leaveTypeId || (opts.leaveTypes[0]?.value ? String(opts.leaveTypes[0].value) : ''),
            reasonId: prev.reasonId || (opts.reasons[0]?.value ? String(opts.reasons[0].value) : ''),
          }));
        }
      })
      .catch(() => {});
  }, [isEdit]);

  // Load balances when employee changes
  useEffect(() => {
    if (form.emp_id) {
      leaveApplicationApi
        .getEmployeeBalances(form.emp_id)
        .then((b) => {
          setBalances(b || []);
        })
        .catch(() => {
          setBalances([]);
        });
    } else {
      setBalances([]);
    }
  }, [form.emp_id]);

  // Load record in edit mode
  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      leaveApplicationApi
        .getApplicationById(editId)
        .then((record) => {
          if (record) {
            setForm({
              applyDate: record.applyDate || new Date().toISOString().slice(0, 10),
              emp_id: record.emp_id ? String(record.emp_id) : '',
              emp_name: record.employee || record.emp_name || '',
              fromDate: record.fromDate || new Date().toISOString().slice(0, 10),
              tillDate: record.tillDate || new Date().toISOString().slice(0, 10),
              type: record.type || 'FullDay',
              leaveTypeId: record.leaveTypeId ? String(record.leaveTypeId) : '',
              reasonId: record.reasonId ? String(record.reasonId) : '',
              remarks: record.remarks || '',
              days: record.days ?? 1.0,
              status: record.status || 'Pending',
            });
          } else {
            appToast.error('Leave application not found.');
            navigate('/payroll/leave-application');
          }
        })
        .catch(() => {
          appToast.error('Failed to load leave application details.');
          navigate('/payroll/leave-application');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [editId, isEdit, navigate]);

  const patchForm = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      // Recalculate days
      if (field === 'type') {
        if (value === 'First Half' || value === 'Second Half') {
          updated.days = 0.5;
        } else {
          // If FullDay, compute difference between fromDate and tillDate
          if (updated.fromDate && updated.tillDate) {
            const start = new Date(updated.fromDate);
            const end = new Date(updated.tillDate);
            const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
            updated.days = diffDays > 0 ? diffDays : 1.0;
          } else {
            updated.days = 1.0;
          }
        }
      } else if (field === 'fromDate' || field === 'tillDate') {
        if (updated.type === 'FullDay') {
          if (updated.fromDate && updated.tillDate) {
            const start = new Date(updated.fromDate);
            const end = new Date(updated.tillDate);
            const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
            updated.days = diffDays > 0 ? diffDays : 1.0;
          }
        }
      }

      return updated;
    });
  };

  const handleSave = async ({ exitAfter = true, customStatus } = {}) => {
    if (!form.emp_id) {
      appToast.error('Please select an employee.');
      return;
    }
    if (!form.leaveTypeId) {
      appToast.error('Please select a leave type.');
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        await leaveApplicationApi.updateApplication(editId, form);
        if (customStatus && customStatus !== form.status) {
          const isAppr = customStatus === 'Approved' ? 1 : customStatus === 'Rejected' ? 2 : 0;
          await leaveApplicationApi.approveApplication(editId, isAppr);
        }
        appToast.success('Leave application updated successfully!');
      } else {
        const created = await leaveApplicationApi.createApplication(form);
        if (customStatus && customStatus === 'Approved' && created?.id) {
          await leaveApplicationApi.approveApplication(created.id, 1);
        }
        appToast.success('Leave application submitted successfully!');
      }

      if (exitAfter) {
        navigate('/payroll/leave-application');
      } else if (!isEdit) {
        setForm({
          ...DEFAULT_FORM_STATE,
          emp_id: form.emp_id,
          leaveTypeId: form.leaveTypeId,
        });
      }
    } catch (err) {
      const errMsg = err?.response?.data?.detail || 'Failed to save leave application.';
      appToast.error(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveAndExit = () => handleSave({ exitAfter: true, customStatus: 'Approved' });
  const handleRejectAndExit = () => handleSave({ exitAfter: true, customStatus: 'Rejected' });

  return {
    state: {
      form,
      balances,
      employeeOptions,
      leaveTypeOptions,
      reasonOptions,
      isEdit,
      editId,
      isLoading,
      isSaving,
    },
    actions: {
      patchForm,
      handleSave,
      handleApproveAndExit,
      handleRejectAndExit,
    },
  };
}
